/** @jsxImportSource @emotion/react */
import { jsx } from '@emotion/react/macro'
import { useEffect, useMemo, useRef, useState } from 'react'
import { uniqueId } from 'lodash'
import { Button, Menu, MenuItem, Tooltip, styled } from '@material-ui/core'
import { ErrorOutline } from '@material-ui/icons'
// import { Notifications } from 'models'

import { AlertSession } from 'lib/types'
import { Orgs } from 'models'
import { trans, TranslationGroup, TranslationKey } from 'lib/types'
import { onAlertNotificationClick } from './utils'
import { useHistory } from 'react-router'
import { getAppColors } from 'lib/constants'
import { AlertDetailDrawer } from 'components/partials/DrawerDetails'
import { AlertSessions, DetailsDrawer } from 'models'
import { ALERTS_PATH } from 'lib/constants'
import { useQuery } from 'models/modelUtils'
import { Howler } from 'howler'
import { playAlertSound } from 'models/Sound'
import { useGlobal } from 'contexts/GlobalProvider'

const notificationIconContent = {
  color: getAppColors('--color-secondary'),
  marginRight: '20px',
}

const activeAlertStyle = {
  backgroundColor: getAppColors('--color-error'),
  color: getAppColors('--color-white'),
  animation: 'blinker 2s linear infinite',
  '@keyframes blinker': {
    '50%': {
      opacity: '0.2',
    },
  },
  '&:hover': {
    backgroundColor: getAppColors('--color-primary'),
  },
}

const menuTitle = {
  padding: '0px 10px 5px 18px',
  width: '300px',
  fontFamily: 'Montserrat',
  fontWeight: 800,
}

const notificationIcon = {
  color: 'inherit',
  marginRight: '5px',
}

const notificationMenuItem = {
  cursor: 'pointer',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  display: 'flex',
  color: 'white',
  width: '100%',
  background: getAppColors('--color-error'),
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  borderRadius: '10px',
  '&:hover': {
    filter: 'grayscale(50%)',
  },
}

const notificationInfo = {
  flex: '1 1 auto',
  padding: '10px 0px 10px 20px',
}

const notificationTitle = {
  fontSize: '1.8rem',
}

const notificationOrg = {
  fontSize: '1.2rem',
}

const LOOP_INTERVAL_MS = 10000
const SAFE_PLAY_DEDUP_MS = 800 // prevents double initial plays from near-simultaneous updates

// Build a stable id for an AlertSession (adjust if your model differs)
const getAlertId = (as: AlertSession) =>
  String(
    (as as any)?.id ??
      (as as any)?.alertSessionId ??
      (as as any)?.uuid ??
      `${(as as any)?.orgId ?? 'no-org'}:${(as as any)?.createdAt ?? (as as any)?.startedAt ?? ''}`,
  )

const NotificationCenter = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const query = useQuery()
  const history = useHistory()
  const translation: TranslationGroup = trans.merge(TranslationKey.HEADER)
  const common: TranslationGroup = trans.common()

  // Optional UI indicator (true while loop is active)
  const [alarmLoopOn, setAlarmLoopOn] = useState(false)
  const { setRefreshFlag } = useGlobal()

  const alertSessions = AlertSessions.use().alertSessions
  const filtered = alertSessions?.filter(as => as.status === 'open') || []
  const count = filtered.length

  // ---- Alarm loop controller (single-interval state machine) ----
  const intervalRef = useRef<number | null>(null)
  const loopActiveRef = useRef(false)
  const seenAlertIdsRef = useRef<Set<string>>(new Set())
  const lastPlayAtRef = useRef(0)
  const processedIdsStrRef = useRef('') // guard against identical replays of the same ids list

  // Derive stable, sorted list of open alert ids
  const openAlertIds = useMemo(() => {
    return filtered.map(getAlertId).sort()
  }, [filtered])

  // Helpers
  const rawPlayOnce = () => {
    try {
      const ctx = (Howler as any)?.ctx
      if (ctx?.state === 'suspended' && typeof ctx?.resume === 'function') {
        ctx.resume().finally(() => playAlertSound())
      } else {
        playAlertSound()
      }
    } catch (e) {
      console.warn('Audio play failed', e)
    }
  }

  const playOnceDedup = () => {
    const now = Date.now()
    if (now - lastPlayAtRef.current < SAFE_PLAY_DEDUP_MS) return
    lastPlayAtRef.current = now
    rawPlayOnce()
  }

  const startLoop = () => {
    if (loopActiveRef.current) return
    loopActiveRef.current = true
    setAlarmLoopOn(true)
    intervalRef.current = window.setInterval(() => {
      const ctx = (Howler as any)?.ctx
      if (ctx?.state !== 'suspended') playAlertSound()
    }, LOOP_INTERVAL_MS)
  }

  const restartLoopTimer = () => {
    if (!loopActiveRef.current) return
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = window.setInterval(() => {
      const ctx = (Howler as any)?.ctx
      if (ctx?.state !== 'suspended') playAlertSound()
    }, LOOP_INTERVAL_MS)
  }

  const stopLoop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (loopActiveRef.current) {
      loopActiveRef.current = false
      setAlarmLoopOn(false)
    }
  }

  // Stop the loop on first user interaction (click or mousemove)
  useEffect(() => {
    const disengage = () => stopLoop()
    window.addEventListener('click', disengage, { passive: true })
    window.addEventListener('mousemove', disengage, { passive: true })
    return () => {
      window.removeEventListener('click', disengage)
      window.removeEventListener('mousemove', disengage)
      stopLoop()
    }
  }, [])

  // React to changes in the set of open alerts
  useEffect(() => {
    const idsStr = openAlertIds.join(',')
    if (idsStr === processedIdsStrRef.current) {
      // identical derived list; ignore (prevents dev/StrictMode replays)
      return
    }
    // mark as processed *early* to avoid re-entrancy doubles
    processedIdsStrRef.current = idsStr

    const curr = new Set(openAlertIds)
    const hasOpen = curr.size > 0

    if (!hasOpen) {
      // No open alerts → stop and reset
      stopLoop()
      seenAlertIdsRef.current = new Set()
      return
    }

    // Find truly new ids vs our seen set
    const currArr = Array.from(curr)
    const seen = seenAlertIdsRef.current
    const newIds = currArr.filter(id => !seen.has(id))

    if (newIds.length > 0) {
      // Update seen FIRST (so any concurrent re-run sees it)
      const updated = new Set(seen)
      for (let i = 0; i < newIds.length; i++) updated.add(newIds[i])
      seenAlertIdsRef.current = updated

      // Single initial play for the batch (deduped)
      playOnceDedup()

      // Ensure a single loop: restart if running, otherwise start
      if (loopActiveRef.current) {
        restartLoopTimer()
      } else {
        startLoop()
      }
    }
    // Else: no new alerts → maintain current loop state (running or disengaged)
  }, [openAlertIds.join(',')])
  // ---- End alarm loop controller ----

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const colorModStyle = count > 0 ? activeAlertStyle : ''

  // this was the only way to simply achieve the intended behavior
  const StyledMenuItem = styled(MenuItem)(() => ({
    cursor: 'default',
    '&:hover': {
      background: 'none',
    },
  }))

  function handleDrawerClose() {
    history.push({
      pathname: `${ALERTS_PATH}`,
      search: query.toString(),
    })
    DetailsDrawer.close()
  }

  const AlertMenuItem = ({ alertSession }: { alertSession: AlertSession }) => {
    return (
      <StyledMenuItem>
        <div
          css={notificationMenuItem}
          onClick={() => {
            handleClose()
            onAlertNotificationClick(alertSession, history)
            localStorage.setItem('selected_org_id', alertSession.orgId)
            setRefreshFlag(true)
            DetailsDrawer.show({
              drawerComponent: AlertDetailDrawer,
              drawerProps: {
                alertSession: alertSession,
                close: handleDrawerClose,
              },
            })
          }}
        >
          <span css={notificationInfo}>
            <div css={notificationTitle}>{translation.alert}</div>
            <div css={notificationOrg}>{Orgs.getOrg(alertSession.orgId)?.name || `${common.loading}`}</div>
          </span>
        </div>
      </StyledMenuItem>
    )
  }

  return (
    <>
      <Tooltip title={translation.notifications}>
        <div css={notificationIconContent}>
          <Button
            name="Open Notifications"
            aria-label="Notification Icon"
            onClick={handleClick}
            css={{
              ...notificationIconContent,
              ...colorModStyle,
            }}
          >
            <ErrorOutline css={notificationIcon} />
            {count}
          </Button>
        </div>
      </Tooltip>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <div css={menuTitle}>{`${translation.notifications.toUpperCase()} (${count})`}</div>
        {filtered?.map(as => (
          <AlertMenuItem key={uniqueId()} alertSession={as} />
        ))}
      </Menu>
    </>
  )
}

export default NotificationCenter
